import OptimizedImage from "@components/ui/OptimizedImage"
import { useRoutePageContext } from "@components/ui/route-page-context"
import { imageAlt, imageRenderProps, imageTitle, localized } from "@lib/component-utils"
import { sanitizeRichHtml } from "@lib/sanitize-html"
import * as m from "@paraglide/messages"
import type { CompanyHistoryItem } from "@site-types/content"
export function CompanyTimeline({ historyItems }: { historyItems: CompanyHistoryItem[] }) {
  const { locale } = useRoutePageContext()
  const history = localized<CompanyHistoryItem>(historyItems, locale).sort((a, b) => {
    const aTitle = a.title ?? ""
    const bTitle = b.title ?? ""
    const yearA = Number.parseInt(aTitle.slice(0, 4), 10)
    const yearB = Number.parseInt(bTitle.slice(0, 4), 10)
    const monthA = Number.parseInt(aTitle.slice(5), 10)
    const monthB = Number.parseInt(bTitle.slice(5), 10)
    if (yearA !== yearB) return yearB - yearA
    return monthB - monthA
  })

  return (
    <section
      className="relative m-auto flex max-w-[800px] flex-col p-[2em] [&_a:hover]:[text-underline-offset:5px] [&_a]:font-bold [&_a]:text-[var(--pink)]"
      data-id="company-timeline"
    >
      <h2>{m.company_companyHistory({}, { locale: locale })}</h2>
      <div className="mt-[5rem] grid w-full grid-cols-1 gap-[3em] bg-[length:10px_calc(100%-40px)] bg-[linear-gradient(var(--pink),var(--pink))] bg-[position:15px_top] bg-no-repeat sm:grid-cols-[1fr_40px_1fr] sm:gap-[1em] sm:bg-[position:center_center]">
        {history.map((entry, index) => (
          <div
            className={`relative z-[5] ml-[7em] max-w-[600px] border-[3px] border-[var(--white)] bg-[var(--white)] p-[2em] [animation-fill-mode:both] [animation-timeline:view(50%_0)] [animation:history-from-right_1s_ease_both] after:absolute after:top-[-13px] after:left-[-10px] after:h-0 after:w-0 after:rotate-[135deg] after:border-t-[10px] after:border-t-transparent after:border-r-[10px] after:border-r-[var(--white)] after:border-b-[10px] after:border-b-transparent after:border-l-[10px] after:border-l-transparent after:content-[''] sm:mr-[7em] sm:ml-0 sm:max-w-[400px] sm:justify-self-end sm:[animation-timeline:view(70%_0)] sm:[animation:history-from-left_1s_ease_both] ${
              index % 2 === 1
                ? "sm:col-[2/span_2] sm:mr-0 sm:ml-[7em] sm:justify-self-start sm:after:right-auto sm:after:left-[-10px] sm:after:border-t-transparent sm:after:border-r-[var(--white)] sm:after:border-b-transparent sm:after:border-l-transparent sm:[animation:history-from-right_1s_ease_both]"
                : "sm:col-[1/span_2] sm:after:right-[-10px] sm:after:left-auto sm:after:border-t-[var(--white)] sm:after:border-r-transparent sm:after:border-b-transparent sm:after:border-l-transparent"
            } ${index === history.length - 1 ? "sm:mb-[-60px]" : ""}`}
            data-id={`company-timeline-entry-${index}`}
            key={entry.id ?? index}
          >
            <h3
              className={`absolute top-[-30px] left-[-80px] h-[60px] w-[60px] bg-[var(--pink)] text-center text-[var(--white)] leading-[60px] ${
                index % 2 === 1
                  ? "sm:left-[calc(-1*5em-60px)] sm:mr-0 sm:ml-[4em]"
                  : "sm:right-[calc(-1*5em-60px)] sm:left-auto sm:mr-[4em]"
              }`}
            >
              {entry.year}
            </h3>
            <p
              className="text-[var(--black)]"
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(entry.description) }}
            />
            {entry.images?.map((image, imageIndex) => {
              const renderProps = imageRenderProps(image, "logo")
              return (
                <OptimizedImage
                  className="mt-[5px] mr-[5px] h-auto max-h-[30px] w-auto"
                  key={`${entry.id ?? index}-${image.asset?.objectKey ?? image.asset?.url ?? imageIndex}`}
                  title={imageTitle(image, locale)}
                  alt={imageAlt(image, locale)}
                  {...renderProps}
                  width={renderProps.width ?? 500}
                  height={renderProps.height ?? 106}
                />
              )
            })}
          </div>
        ))}
        {history.length % 2 === 0 ? <div data-id="company-timeline-spacer" /> : null}
      </div>
    </section>
  )
}

export default CompanyTimeline
